/**
 * PLACES-BRIEF — enter a work, map is the stage.
 * Wave A survey + Wave B Charter (Site fallback Rank + Mandate).
 * Soft≠Live. Ontology: docs/plans/2026-08-20-site-zone-group-machine-ontology-freeze.md
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { html, Ico, stay } from "./ui.js";
import { useApi } from "./api.js";
import { SitesSettingsDesk } from "./sites-settings-desk.js";
import { FleetZonesDesk } from "./fleet-zones-desk.js";
import { PlacesInkBar } from "./places-ink-bar.js";
import { PlacesCharterWaveB } from "./places-charter-wave-b.js";
import { fleetZoneDevicesOf, fleetZoneListOf } from "./fleet-zones-desk.js";
import { deviceHomedOnSite } from "./ops-map-region-filter.js";
import { placesWorkGapsOf, placesEntityToneOf } from "./places-gaps.js";
import { ViewportSheet } from "./viewport-sheet.js";

function siteLabelOf(site) {
  if (!site) return "No site yet";
  return String(site.label || site.name || site.site_id || site.id || "Work").trim() || "Work";
}

/** Catalogue picks the work. The string "default" is not a stand-in for a missing site. */
function workingSiteId(sites, current) {
  const rows = (sites || []).filter((site) => siteIdOf(site) && String(site.status || "active") !== "retired");
  const cur = String(current || "").trim();
  if (cur && rows.some((site) => siteIdOf(site) === cur)) return cur;
  const marked = rows.find((site) => site.is_default === true);
  return siteIdOf(marked) || siteIdOf(rows[0]) || "";
}

function siteIdOf(site) {
  return String((site && (site.site_id || site.id)) || "").trim();
}

export default function PlacesPrimary({ sub, toast }) {
  const routeAt = sub === "sites" || sub === "groups" || sub === "zones" ? sub : "zones";
  /* stay() updates the hash without a hashchange, so tab switches must be local.
     Deep links still arrive through routeAt. */
  const [mode, setMode] = useState(routeAt);
  useEffect(() => { setMode(routeAt); }, [routeAt]);
  const sitesApi = useApi("/resource-fabric/sites");
  const sites = useMemo(
    () => ((sitesApi.data && sitesApi.data.items) || []).filter(Boolean),
    [sitesApi.data],
  );
  const [siteId, setSiteId] = useState("");
  const [cardIn, setCardIn] = useState(true);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchDrop, setSwitchDrop] = useState("down");
  const [zonesOpen, setZonesOpen] = useState(routeAt === "zones");
  const switchRef = useRef(null);

  const workId = workingSiteId(sites, siteId);
  const active = useMemo(
    () => sites.find((s) => siteIdOf(s) === workId) || null,
    [sites, workId],
  );
  const workName = siteLabelOf(active);

  useEffect(() => {
    if (workId !== siteId) setSiteId(workId);
  }, [workId, siteId]);

  useEffect(() => {
    setCardIn(true);
    const t = setTimeout(() => setCardIn(false), 1180);
    return () => clearTimeout(t);
  }, [workId]);

  useEffect(() => {
    if (!switchOpen) return undefined;
    const onDoc = (ev) => {
      const root = switchRef.current;
      if (root && ev.target && !root.contains(ev.target)) setSwitchOpen(false);
    };
    const onKey = (ev) => { if (ev.key === "Escape") setSwitchOpen(false); };
    const place = () => {
      const root = switchRef.current;
      if (!root) return;
      const r = root.getBoundingClientRect();
      const menu = root.querySelector(".places-brief__switch-menu");
      const need = Math.max(180, menu ? menu.getBoundingClientRect().height : 220) + 12;
      const below = window.innerHeight - r.bottom;
      const above = r.top;
      setSwitchDrop(below < need && above > below ? "up" : "down");
    };
    place();
    const raf = requestAnimationFrame(place);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [switchOpen, sites.length]);

  const devicesApi = useApi("/devices");
  const zonesApi = useApi(
    workId ? `/zones?site_id=${encodeURIComponent(workId)}` : "",
  );
  const siteDevices = useMemo(() => {
    const all = fleetZoneDevicesOf(devicesApi.data);
    return all.filter((d) => deviceHomedOnSite(d, workId));
  }, [devicesApi.data, workId]);
  const surveyed = useMemo(() => fleetZoneListOf(zonesApi.data), [zonesApi.data]);
  const gaps = useMemo(
    () => placesWorkGapsOf(siteDevices, surveyed),
    [siteDevices, surveyed],
  );

  const charterOpen = mode === "sites";
  const inkFocus = mode === "groups";

  const goZones = () => {
    setMode("zones");
    stay("/places/zones");
    setZonesOpen(true);
  };
  const goGroups = () => {
    setMode("groups");
    setZonesOpen(false);
    stay("/places/groups");
  };
  const goCharter = () => {
    setMode("sites");
    setZonesOpen(false);
    stay("/places/sites");
  };

  const railOpen = inkFocus || zonesOpen;
  const railMode = inkFocus ? "inks" : "zones";

  const pickWork = (id) => {
    setSiteId(id);
    setSwitchOpen(false);
    setCardIn(true);
    if (charterOpen) stay("/places/sites");
    else if (inkFocus) stay("/places/groups");
    else stay("/places/zones");
  };

  const hasWarn = !!(gaps.offMap || gaps.offRing || gaps.mismatch || gaps.conflict);
  const surveyTone = hasWarn ? "warn" : (gaps.ungrouped ? "dim" : "go");
  const surveyLabel = hasWarn
    ? "Survey needs attention"
    : gaps.ungrouped
      ? "Survey open · some boards ungrouped"
      : "Survey clear";

  return html`<div className="sheet places-primary places-brief desk-craft house-rack"
    data-cmc-places="places-brief" data-chrome="mast" data-fluid="1" data-fullbleed="1"
    data-charter=${charterOpen ? "1" : "0"} data-ink=${inkFocus ? "1" : "0"}
    data-survey=${surveyTone} data-cmc-work-site=${workId}>

    <div className="places-brief__titlecard" data-show=${cardIn ? "1" : "0"} aria-hidden=${cardIn ? "false" : "true"}>
      <p>Enter this site</p>
      <h2>${workName}</h2>
      <span>${workId || "No site yet"}</span>
      <em>${gaps.zoneCount} zones · ${gaps.groupCount} inks · ${gaps.deviceCount} boards</em>
    </div>

    <div className="wrap places-brief__wrap">
      <header className="places-brief__mast" aria-label="Survey mast" data-compact="1"
        style=${{ "--site-tone": placesEntityToneOf(workId, "site") }}>
        <div className="places-brief__mast-main">
          <div className="places-brief__lock">
            <i className="places-brief__live" aria-hidden="true"></i>
            <div className="places-brief__brand">
              <div className="places-brief__titleline">
                <span className="eyebrow">House · draw the site</span>
                <h1>${workName}</h1>
                ${workId ? html`<code>${workId}</code>` : null}
              </div>
              <div className="places-brief__meters" aria-label="Work meters">
                <b>${gaps.zoneCount}<small>zones</small></b>
                <b>${gaps.groupCount}<small>inks</small></b>
                <b>${gaps.deviceCount}<small>boards</small></b>
              </div>
            </div>
          </div>
          <div className="places-brief__survey" data-tone=${surveyTone} title=${surveyLabel}>
            <span className="places-brief__lamp" data-tone=${surveyTone} aria-hidden="true"></span>
            <div className="places-brief__gaps" aria-label="Work gaps">
              ${gaps.offMap ? html`<i data-tone="warn">Off map ${gaps.offMap}</i>` : null}
              ${gaps.offRing ? html`<i data-tone="warn">Off ring ${gaps.offRing}</i>` : null}
              ${gaps.ungrouped ? html`<i data-tone="dim">No group ${gaps.ungrouped}</i>` : null}
              ${gaps.mismatch ? html`<i data-tone="warn">Mismatch ${gaps.mismatch}</i>` : null}
              ${gaps.conflict ? html`<i data-tone="warn">Overlap ${gaps.conflict}</i>` : null}
              ${!hasWarn && !gaps.ungrouped ? html`<i data-tone="go">Clear</i>` : null}
            </div>
          </div>
        </div>

        <div className="places-brief__mast-ops">
          <div className="places-brief__switch" ref=${switchRef}
            data-open=${switchOpen ? "1" : "0"} data-drop=${switchDrop}>
            <button type="button" className="places-brief__site-btn places-brief__act"
              aria-expanded=${switchOpen ? "true" : "false"}
              aria-label=${`Switch work · ${workName}`}
              onClick=${() => setSwitchOpen((v) => !v)}>
              <i className="places-brief__swatch" aria-hidden="true"></i>
              <span>${workName}</span>
              <${Ico} d="layers" w=${12}/>
            </button>
            ${switchOpen ? html`<div className="places-brief__switch-menu" role="listbox">
              ${sites.length
                ? sites.map((s) => {
                  const id = siteIdOf(s);
                  return html`<button key=${id} type="button" role="option"
                    className="places-brief__act"
                    aria-selected=${id === workId ? "true" : "false"}
                    data-on=${id === workId ? "1" : "0"}
                    style=${{ "--ztone": placesEntityToneOf(id, "site") }}
                    onClick=${() => pickWork(id)}>
                    <i className="places-brief__swatch" aria-hidden="true"></i>
                    <span><b>${siteLabelOf(s)}</b><small>${id}</small></span>
                  </button>`;
                })
                : html`<p className="note">No works yet — open Charter to bootstrap.</p>`}
            </div>` : null}
          </div>
          <div className="places-brief__modes" role="tablist" aria-label="Work modes">
            <button type="button" role="tab" className="places-brief__mode places-brief__act"
              aria-selected=${!charterOpen && !inkFocus ? "true" : "false"}
              data-on=${!charterOpen && !inkFocus ? "1" : "0"}
              onClick=${goZones}><${Ico} d="map" w=${13}/> Zones</button>
            <button type="button" role="tab" className="places-brief__mode places-brief__act"
              aria-selected=${charterOpen ? "true" : "false"}
              data-on=${charterOpen ? "1" : "0"}
              onClick=${goCharter}><${Ico} d="user" w=${13}/> Charter</button>
            <button type="button" role="tab" className="places-brief__mode places-brief__act"
              aria-selected=${inkFocus ? "true" : "false"}
              data-on=${inkFocus ? "1" : "0"}
              onClick=${inkFocus ? goZones : goGroups}><${Ico} d="layers" w=${13}/> Inks</button>
          </div>
        </div>
      </header>

      <main className="places-brief__stage" data-keel="1" data-bridge="1"
        data-rail=${railOpen ? "1" : "0"} data-mode=${railMode}>
        <div className="places-brief__canvas" data-braid=${gaps.conflict ? "1" : "0"}>
          <${FleetZonesDesk} toast=${toast} siteId=${workId} embedded=${true}
            floatOpen=${zonesOpen && !inkFocus} onFloatOpen=${setZonesOpen}
            onOpenGroups=${goGroups}/>
        </div>
        ${inkFocus ? html`<aside className="places-brief__bridge places-brief__bridge--inks" data-open="1"
          aria-label="Ink instrument">
          <header className="places-brief__bridge-head">
            <div>
              <span>Ink instrument</span>
              <b>Inks · machine stamps</b>
              <small>Stamp here — map keeps the work in view.</small>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick=${goZones}>
              <${Ico} d="close" w=${14}/> Map
            </button>
          </header>
          <${PlacesInkBar} toast=${toast} siteId=${workId} focus=${true}
            onOpenZones=${goZones} floating=${false}/>
        </aside>` : null}
      </main>
    </div>

    ${charterOpen ? html`<${ViewportSheet}
        title=${"Charter · " + workName}
        wide=${true}
        dock="right"
        onClose=${goZones}>
        <p className="note">People on this work. A site can exist with no members. Rank and Mandate stay under Advanced — they are not first-run homework.</p>
        <details className="ai-studio__advanced" data-cmc-places-rank-advanced="1">
          <summary>Advanced rank (later)</summary>
          <${PlacesCharterWaveB} siteId=${workId} toast=${toast}/>
        </details>
        <${SitesSettingsDesk} toast=${toast} showZones=${false} context="places"
          selectedSiteId=${workId} onSelectSite=${(id) => setSiteId(String(id || ""))}/>
      <//>` : null}
  </div>`;
}
